import React, { useCallback, useEffect, useState } from 'react';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { ActionIcon, Button, Input, Modal, Textarea, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { ProfessionalAreaService } from '../../../services/professional-area/professionalAreaService';

const columnOrder = ['id', 'code', 'name', 'description'];

const ProfessionalAreaModalForm: React.FC<{
    data: {
        id?: string;
        code?: string;
        name?: string;
        description?: string | null;
    } | null;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}> = ({ data, isOpen, onClose, refetch }) => {
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const { handleErrors } = useHandleErrors();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setCode(data?.code ?? '');
        setName(data?.name ?? '');
        setDescription(data?.description ?? '');
    }, [isOpen, data]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!code.trim()) {
            toast.error('El código es obligatorio');
            return;
        }

        if (!name.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }

        setLoading(true);
        try {
            const service = new ProfessionalAreaService();
            const response = data?.id
                ? await (await service.edit({
                    id: data.id,
                    code: code.trim(),
                    name: name.trim(),
                    description: description.trim() || null,
                })).getResponseData()
                : await (await service.create({
                    code: code.trim(),
                    name: name.trim(),
                    description: description.trim() || null,
                })).getResponseData();

            if (response?.success) {
                toast.success(data?.id ? 'Área profesional actualizada correctamente' : 'Área profesional creada correctamente');
                onClose();
                refetch();
            } else {
                handleErrors(response);
            }
        } catch {
            toast.error('Error al guardar el área profesional');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <h3 className="text-lg font-semibold">
                    {data?.id ? 'Editar Área Profesional' : 'Crear Área Profesional'}
                </h3>

                <Input
                    id="code"
                    label="Código"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                />
                <Input
                    id="name"
                    label="Nombre"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
                <Textarea
                    id="description"
                    label="Descripción"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                />

                <div className="flex gap-2 justify-end pt-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                        Cancelar
                    </button>
                    <button type="submit" disabled={loading} className="px-4 py-2 bg-primary text-white rounded">
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

const ProfessionalAreasList = () => {
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const service = new ProfessionalAreaService();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    const [data, loading, , refetch] = useFetch(useCallback(async () => {
        const response = await service.list(filters);
        return response.getResponseData();
    }, [filters]));

    const handleDelete = async (id: string) => {
        const response = await (await service.delete(id)).getResponseData();
        if (response?.success) {
            toast.success('Área profesional eliminada correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleEdit = useCallback((row: any) => {
        setSelectedItem({
            id: row.id,
            code: row.code,
            name: row.name,
            description: row.description ?? '',
        });
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedItem(null);
    }, []);

    const orderedData = () => data?.data?.map((row: any) => ({
        id: row.id,
        code: row.code,
        name: row.name,
        description: row.description ?? '',
    })) ?? [];

    return (
        <div className="space-y-6">
            {data !== undefined && (
                <CustomTable
                    largeTable
                    id="professional-areas-table"
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    filter={false}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data?.lastPage ?? 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'products', permission: 'admin_products' },
                            render: (row: any) => (
                                <Tooltip size="sm" content="Editar" placement="top" color="invert">
                                    <ActionIcon
                                        as="span"
                                        size="sm"
                                        variant="outline"
                                        className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                        onClick={() => handleEdit(row)}
                                    >
                                        <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            ),
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'products', permission: 'admin_products' },
                            render: (row: any) => (
                                <Tooltip size="sm" content="Eliminar" placement="top" color="invert">
                                    <div>
                                        <DeletePopover
                                            title="Eliminar área profesional"
                                            description={`¿Estás seguro de que deseas eliminar "${row.name}"?`}
                                            onDelete={() => handleDelete(row.id)}
                                        />
                                    </div>
                                </Tooltip>
                            ),
                        },
                    ]}
                >
                    {userCan('admin_products', 'products') && (
                        <Button onClick={() => { setSelectedItem(null); setIsModalOpen(true); }}>
                            Crear área profesional
                        </Button>
                    )}
                </CustomTable>
            )}

            <ProfessionalAreaModalForm
                data={selectedItem}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                refetch={refetch}
            />
        </div>
    );
};

export default ProfessionalAreasList;
